import { useState } from 'react'
import { NoData } from 'components/NoData'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { BackHeader } from 'components/ui/BackHeader/BackHeader'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { BannerText, BannerWrap } from 'pages/Operations/Shared/opsShared.styled'
import { Flex } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import {
    IInventoryCountLine,
    IStockTakeDetailProps,
} from 'types/inventoryControl.interface'
import { formatGhs } from 'pages/FuelManagement/fuelOpsDemoData'
import {
    countProgressLabel,
    deviationBannerText,
    lineOverDeviationLimit,
    lineVarianceGhs,
    lineVarianceQty,
    signedGhs,
    stockTakeVarianceTone,
    STOCK_TAKE_APPROVAL_BANNER,
    STOCK_TAKE_APPROVER_ROLE,
    STOCK_TAKE_BLIND_CAPTION,
    STOCK_TAKE_DEVIATION_CAPTION,
    STOCK_TAKE_STATUS_META,
    STOCK_TAKE_SUPERVISOR_ROLE,
    STOCK_TAKE_VARIANCE_CAPTION,
} from '../inventoryControlDemoData'
import {
    ApproverLine,
    CellValue,
    CountInput,
    DetailMetaLine,
    MaskedValue,
    MicroNote,
    OverLimitTag,
    SheetCaption,
    SheetNoteText,
    VarianceValue,
} from './InventoryStockTakes.styled'

const CountCell = ({
    line,
    masked,
    onCount,
}: {
    line: IInventoryCountLine
    masked: boolean
    onCount: IStockTakeDetailProps['onCount']
}) => {
    const [value, setValue] = useState('')

    const commit = () => {
        const qty = Number(value)
        if (value !== '' && !Number.isNaN(qty) && qty >= 0) {
            onCount(line, qty)
        }
    }

    return (
        <CountInput
            type='number'
            value={value}
            placeholder={masked ? '•••' : `${line.systemQty}`}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur()
                }
            }}
            onClick={(e) => e.stopPropagation()}
        />
    )
}

const varianceToneOf = (qty: number | null): 'danger' | 'positive' | 'normal' | 'dim' => {
    if (qty == null) return 'dim'
    if (qty < 0) return 'danger'
    if (qty > 0) return 'positive'
    return 'normal'
}

export const StockTakeDetail = ({
    stockTake,
    lines,
    entries,
    onCount,
    onSubmitForApproval,
    onApprove,
    onBack,
}: IStockTakeDetailProps) => {
    const mergedLines: IInventoryCountLine[] = lines.map((line) =>
        line.countedQty == null && entries[line.id] != null ? { ...line, countedQty: entries[line.id] } : line,
    )

    const localCounted = lines.filter((line) => line.countedQty == null && entries[line.id] != null)
    const localVariance = localCounted.reduce(
        (sum, line) => sum + (entries[line.id] - line.systemQty) * line.unitCost,
        0,
    )
    const counted = stockTake.countedLines + localCounted.length
    const variance =
        stockTake.varianceGhs == null && localCounted.length === 0
            ? null
            : (stockTake.varianceGhs ?? 0) + localVariance

    const meta = STOCK_TAKE_STATUS_META[stockTake.status]
    const editable = stockTake.status === 'IN_PROGRESS'
    const subset = stockTake.totalLines != null && mergedLines.length > 0 && mergedLines.length < stockTake.totalLines

    const blindActive = stockTake.blind === true && stockTake.status === 'IN_PROGRESS'
    const sessionLineIds = new Set(lines.filter((line) => line.countedQty == null).map((line) => line.id))
    const isMasked = (line: IInventoryCountLine): boolean => blindActive && sessionLineIds.has(line.id)
    const deviationLimit = stockTake.deviationLimitGhs ?? null
    const thresholdsRevealed =
        deviationLimit != null && (stockTake.status === 'AWAITING_APPROVAL' || stockTake.status === 'COMPLETED')
    const breachedCount = thresholdsRevealed
        ? mergedLines.filter((line) => lineOverDeviationLimit(line, deviationLimit)).length
        : 0
    const supervisorRequired = stockTake.status === 'AWAITING_APPROVAL' && breachedCount > 0

    const columns: IHonestTableColumn<IInventoryCountLine>[] = [
        { id: 'sku', header: 'SKU', cell: (line) => <CellValue>{line.sku}</CellValue> },
        { id: 'item', header: 'ITEM', minWidth: '14rem', cell: (line) => line.item },
        {
            id: 'systemQty',
            header: 'SYSTEM QTY',
            align: 'right',
            cell: (line) =>
                isMasked(line) ? <MaskedValue>•••</MaskedValue> : <CellValue>{line.systemQty}</CellValue>,
        },
        {
            id: 'countedQty',
            header: 'COUNTED',
            align: 'right',
            cell: (line) =>
                line.countedQty != null ? (
                    <CellValue $em>{line.countedQty}</CellValue>
                ) : editable ? (
                    <CountCell line={line} masked={blindActive} onCount={onCount} />
                ) : (
                    <OtherText grey>—</OtherText>
                ),
        },
        {
            id: 'unitCost',
            header: 'UNIT COST',
            align: 'right',
            cell: (line) => <CellValue>{`GH₵ ${line.unitCost.toLocaleString('en-US')}`}</CellValue>,
        },
        {
            id: 'varianceQty',
            header: 'VARIANCE QTY',
            align: 'right',
            cell: (line) => {
                if (isMasked(line)) return <MaskedValue>•••</MaskedValue>
                const qty = lineVarianceQty(line)
                return (
                    <VarianceValue $tone={varianceToneOf(qty)}>
                        {qty == null ? '—' : qty > 0 ? `+${qty}` : `${qty}`.replace('-', '−')}
                    </VarianceValue>
                )
            },
        },
        {
            id: 'varianceGhs',
            header: 'VARIANCE GH₵',
            align: 'right',
            cell: (line) => {
                if (isMasked(line)) return <MaskedValue>•••</MaskedValue>
                const value = lineVarianceGhs(line)
                return (
                    <>
                        <VarianceValue $tone={varianceToneOf(value)}>
                            {value == null ? '—' : signedGhs(value)}
                        </VarianceValue>
                        {thresholdsRevealed && lineOverDeviationLimit(line, deviationLimit) && (
                            <OverLimitTag>Over limit</OverLimitTag>
                        )}
                    </>
                )
            },
        },
        {
            id: 'note',
            header: 'NOTE',
            minWidth: '16rem',
            cell: (line) => (line.note ? <SheetNoteText>{line.note}</SheetNoteText> : <OtherText grey>—</OtherText>),
        },
    ]

    return (
        <>
            <BackHeader title={stockTake.name} onBack={onBack}>
                <Flex center gap='0.5rem'>
                    {stockTake.blind === true && <StatusPill status='neutral'>BLIND</StatusPill>}
                    <StatusPill status={meta.tone}>{meta.label}</StatusPill>
                    {editable && (
                        <Button size='sm' variant='secondary' onClick={() => onSubmitForApproval(stockTake)}>
                            Submit for approval
                        </Button>
                    )}
                </Flex>
            </BackHeader>
            <Flex column gap='0.25rem' mb='16'>
                <DetailMetaLine>
                    {`${stockTake.location} · Scheduled ${stockTake.scheduledLabel}${stockTake.startedLabel ? ` · Started ${stockTake.startedLabel}` : ''} · Counted ${countProgressLabel(counted, stockTake.totalLines)}${deviationLimit != null ? ` · Deviation limit ${formatGhs(deviationLimit)}` : ''}`}
                </DetailMetaLine>
                {variance != null &&
                    (blindActive ? (
                        <Flex center gap='0.5rem'>
                            <OtherText grey>Variance</OtherText>
                            <MaskedValue>•••</MaskedValue>
                        </Flex>
                    ) : (
                        <VarianceValue $tone={stockTakeVarianceTone(variance, stockTake.sheetValueGhs) === 'danger' ? 'danger' : variance > 0 ? 'positive' : 'normal'}>
                            {`Variance ${signedGhs(variance)}`}
                        </VarianceValue>
                    ))}
                {blindActive && <MicroNote>{STOCK_TAKE_BLIND_CAPTION}</MicroNote>}
            </Flex>

            {stockTake.status === 'AWAITING_APPROVAL' && (
                <BannerWrap $tone={supervisorRequired ? 'danger' : 'warning'}>
                    <Flex column gap='0.25rem'>
                        <BannerText $tone={supervisorRequired ? 'danger' : 'warning'}>
                            {supervisorRequired
                                ? deviationBannerText(breachedCount, deviationLimit)
                                : STOCK_TAKE_APPROVAL_BANNER}
                        </BannerText>
                        <OtherText grey>
                            {`Approver — ${supervisorRequired ? STOCK_TAKE_SUPERVISOR_ROLE : STOCK_TAKE_APPROVER_ROLE}`}
                        </OtherText>
                    </Flex>
                    <Button size='sm' variant='primary' onClick={() => onApprove(stockTake)}>
                        {supervisorRequired ? 'Approve as supervisor' : 'Approve adjustments'}
                    </Button>
                </BannerWrap>
            )}
            {stockTake.status === 'COMPLETED' && (
                <Flex mb='16'>
                    <ApproverLine>
                        {`✓ Adjustments posted to stock · Approved by — ${stockTake.approvedBy ?? STOCK_TAKE_APPROVER_ROLE}, ${stockTake.approvedAt ?? '—'}`}
                    </ApproverLine>
                </Flex>
            )}

            <Container>
                <ContainerHeader>
                    <Flex center gap='0.75rem'>
                        <TitleText>Count sheet</TitleText>
                        <DemoBadge />
                    </Flex>
                </ContainerHeader>
                <ContainerContent>
                    {!mergedLines.length ? (
                        <NoData text='Count sheet is generated when the count starts.' />
                    ) : (
                        <>
                            <HonestTable
                                columns={columns}
                                data={mergedLines}
                                keyField='id'
                                emptyMessage='Count sheet is generated when the count starts.'
                            />
                            {subset && (
                                <SheetCaption>
                                    {`Showing ${mergedLines.length} of ${stockTake.totalLines} lines — variance lines first.`}
                                </SheetCaption>
                            )}
                            {stockTake.status === 'AWAITING_APPROVAL' && deviationLimit != null && (
                                <SheetCaption>{STOCK_TAKE_DEVIATION_CAPTION}</SheetCaption>
                            )}
                            <SheetCaption>{STOCK_TAKE_VARIANCE_CAPTION}</SheetCaption>
                        </>
                    )}
                </ContainerContent>
            </Container>
        </>
    )
}
